import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  ArrowRight,
  Building2,
  Waves,
  CloudRain,
  Ship,
  Droplets,
  TrendingDown,
  Minus,
  TrendingUp,
  AlertTriangle,
  Layers,
  Square,
  Filter,
  HelpCircle,
  Bug,
  Droplet,
  Circle,
  RefreshCw,
  Zap,
  Clock,
  Factory,
  Globe,
  Leaf,
  Mountain,
  Wrench,
  Sparkles,
  Calendar,
  AlertCircle,
  CalendarDays,
  CalendarRange,
  Truck,
  Shield,
  CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Building2, Waves, CloudRain, Ship, Droplets,
  TrendingDown, Minus, TrendingUp, AlertTriangle,
  Layers, Square, Filter, HelpCircle, Bug, Droplet,
  Circle, RefreshCw, Zap, Clock, Factory, Globe,
  Leaf, Mountain, Wrench, Sparkles, Calendar,
  AlertCircle, CalendarDays, CalendarRange, Truck,
  Shield, CheckCircle
};

const QuestionnairePage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestions();
  }, [category]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions?category=${category}`);
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const totalSteps = questions.length;
  const isLastStep = currentStep === totalSteps - 1;

  const handleOptionSelect = (criteriaKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [criteriaKey]: value
    }));
  };

  const handleNext = () => {
    if (isLastStep) {
      // Navigate to recommendation with answers
      navigate(`/recommendation/${category}`, { 
        state: { answers } 
      });
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate("/");
    }
  };

  const canProceed = currentQuestion && answers[currentQuestion.criteria_key];

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">No Questions Available</h2>
          <p className="text-slate-600 mb-6">
            Questions for this category are not yet configured. Please try another category.
          </p>
          <Link to="/">
            <Button className="bg-[#00BCD4] hover:bg-[#00acc1]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-80px)] flex flex-col" data-testid="questionnaire-page">
        {/* Progress Header */}
        <div className="bg-white border-b border-slate-200 py-4">
          <div className="max-w-4xl mx-auto px-4">
            <button 
              onClick={handleBack}
              className="inline-flex items-center text-slate-600 hover:text-[#00BCD4] font-medium transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </button>
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <div className="max-w-2xl w-full">
            {/* Question Number */}
            <div className="text-center mb-8">
              <span className="text-sm font-bold tracking-wider text-slate-400 uppercase">
                Question {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Question Text */}
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-10 animate-fadeIn">
              {currentQuestion?.question_text}
            </h2>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {currentQuestion?.options.map((option, index) => {
                const IconComponent = iconMap[option.icon] || HelpCircle;
                const isSelected = answers[currentQuestion.criteria_key] === option.value;
                
                return (
                  <button
                    key={option.id}
                    onClick={() => handleOptionSelect(currentQuestion.criteria_key, option.value)}
                    className={`question-option ${isSelected ? 'selected' : ''} animate-fadeIn`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`option-${option.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-[#00BCD4]' : 'bg-slate-100'
                      }`}>
                        <IconComponent className={`w-6 h-6 ${
                          isSelected ? 'text-white' : 'text-slate-500'
                        }`} />
                      </div>
                      <span className={`text-lg font-medium ${
                        isSelected ? 'text-[#00BCD4]' : 'text-slate-700'
                      }`}>
                        {option.text}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Next Button */}
            <div className="text-center">
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className="bg-[#00BCD4] hover:bg-[#00acc1] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 text-lg"
                data-testid="next-button"
              >
                {isLastStep ? "Get Recommendation" : "Next"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Progress Dots */}
            <div className="wizard-steps mt-10">
              {questions.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${index === currentStep ? 'active' : 'inactive'} ${
                    index < currentStep ? 'bg-[#00BCD4]/50' : ''
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QuestionnairePage;
